import { ReactElement } from 'react';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';

import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import { FileViewWithPreview } from '@hh.ru/magritte-ui-upload/FileViewWithPreview';
import { Placeholder } from '@hh.ru/magritte-ui-upload/Placeholder';
import { UploadProps } from '@hh.ru/magritte-ui-upload/types';
import { useDropActions } from '@hh.ru/magritte-ui-upload/useDropActions';
import { useDropButtons } from '@hh.ru/magritte-ui-upload/useDropButtons';
import {
    getUploadIcon,
    getAvaliableFileTypes,
    RenderFileInfoDefault,
    DEFAULT_ACCEPT,
} from '@hh.ru/magritte-ui-upload/utils';

import styles from './upload.less';

const MultiUpload = ({
    files,
    trls,
    size,
    accept = DEFAULT_ACCEPT,
    selectFileButton: _selectFileButton,
    renderFileInfo = RenderFileInfoDefault,
    onDropAccepted: onDropAcceptedExternal,
    onDropRejected: onDropRejectedExternal,
    onFileDelete: onFileDeleteExternal,
    'aria-label': ariaLabel,
    ...dropzoneProps
}: Omit<UploadProps, 'multiple'>): ReactElement => {
    const uploadType = getAvaliableFileTypes(accept);
    const { onDropAccepted, onDropRejected, onFileDelete } = useDropActions({
        onDropAccepted: onDropAcceptedExternal,
        onDropRejected: onDropRejectedExternal,
        onFileDelete: onFileDeleteExternal,
    });
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        ...dropzoneProps,
        accept,
        onDropAccepted,
        onDropRejected,
        multiple: true,
    });
    const { selectFileButton } = useDropButtons({ selectFileButton: _selectFileButton });
    return (
        <>
            <div
                className={classnames(styles.root, styles[`root_${size}`], { [styles.dragActive]: isDragActive })}
                {...getRootProps()}
                data-qa="magritte-multiple-upload"
            >
                <input {...getInputProps()} data-qa="magritte-multiple-upload-input" />
                <div className={styles.background} />
                <div className={styles.body}>
                    {getUploadIcon(uploadType, undefined, false)}
                    <VSpacing default={16} />
                    <Placeholder trls={trls} selectFileButton={selectFileButton} />
                </div>
            </div>
            {files.length > 0 && (
                <div className={styles.fileList}>
                    {files.map((loadedFile, indexFile) => {
                        return (
                            <FileViewWithPreview
                                key={loadedFile.data.name}
                                loadedFile={loadedFile}
                                renderFileInfo={renderFileInfo}
                                onFileDelete={(event) => {
                                    onFileDelete(event, loadedFile, indexFile);
                                }}
                                aria-label={ariaLabel || ''}
                                isLast={files.length - 1 === indexFile}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );
};

export { MultiUpload };
